import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  Presentation,
  MessageSquare,
  FileText,
  Truck,
  PackageCheck,
  Users,
  UserCheck,
  Receipt,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ClipboardList,
  BookOpen,
  Settings,
  ShieldCheck,
  Warehouse,
  Tag,
  ScrollText,
  Star,
  FileSpreadsheet,
} from "lucide-react";
import NotificationBell from "./NotificationBell";
import PWAInstallButton from "./PWAInstallButton";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_6e85a7b3-7844-4782-8600-31defd3a9a43/artifacts/5fhyey8q_MV%20trasporti%20logo.png";

const navItems = [
  { path: "/admin", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/admin/contatti", icon: MessageSquare, label: "Contatti" },
  { path: "/admin/preventivi", icon: FileText, label: "Preventivi" },
  { path: "/admin/presentazione", icon: Presentation, label: "Presentazione" },
  { path: "/admin/spedizioni", icon: Truck, label: "Spedizioni" },
  { path: "/admin/ritiri", icon: PackageCheck, label: "Ritiri" },
  { path: "/admin/distinte", icon: ClipboardList, label: "Distinte" },
  { path: "/admin/etichette", icon: Tag, label: "Etichette" },
  { path: "/admin/autisti", icon: UserCheck, label: "Autisti" },
  { path: "/admin/fatturazione", icon: Receipt, label: "Fatturazione" },
  { path: "/admin/trasferte", icon: FileSpreadsheet, label: "Prospetto Viaggi" },
  { path: "/admin/magazzino", icon: Warehouse, label: "Magazzino" },
  { path: "/admin/clienti", icon: Users, label: "Clienti" },
  { path: "/admin/blog", icon: BookOpen, label: "Blog" },
  { path: "/admin/governance-dati", icon: ShieldCheck, label: "Governance Dati" },
  { path: "/admin/audit-log", icon: ScrollText, label: "Audit Log" },
  { path: "/admin/recensioni", icon: Star, label: "Recensioni" },
  { path: "/admin/impostazioni", icon: Settings, label: "Impostazioni" },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [admin, setAdmin] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("mv_admin_token");
    const name = localStorage.getItem("mv_admin_name");
    if (!token) {
      const next = `${location.pathname}${location.search}`;
      navigate(next && next !== "/admin" ? `/admin/login?next=${encodeURIComponent(next)}` : "/admin/login");
      return;
    }
    setAdmin({ name });
  }, [navigate, location.pathname, location.search]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location]);

  const handleLogout = () => {
    localStorage.removeItem("mv_admin_token");
    localStorage.removeItem("mv_admin_name");
    navigate("/admin/login");
  };

  if (!admin) return null;

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#09090B] transform transition-transform duration-300 lg:relative lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-zinc-800">
            <Link to="/admin" className="block">
              <img src={LOGO_URL} alt="MV Trasporti" className="h-10 w-auto invert" />
            </Link>
            <p className="text-xs text-zinc-500 mt-2 font-heading tracking-widest uppercase">
              Pannello Admin
            </p>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`admin-nav-${item.label.toLowerCase()}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-bold tracking-wide uppercase transition-colors duration-200 ${
                    isActive
                      ? "bg-[#FF5200] text-white"
                      : "text-zinc-400 hover:text-white hover:bg-zinc-800"
                  }`}
                >
                  <item.icon size={18} />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-zinc-800">
            <div className="flex items-center gap-3 px-4 py-2 mb-2">
              <div className="w-8 h-8 bg-[#FF5200] rounded-full flex items-center justify-center text-white text-sm font-bold">
                {admin.name?.charAt(0) || "A"}
              </div>
              <span className="text-sm text-zinc-300 font-body">{admin.name}</span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-bold tracking-wide uppercase text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors duration-200 w-full"
            >
              <LogOut size={18} /> Esci
            </button>
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 rounded-sm text-sm tracking-wide text-zinc-500 hover:text-zinc-300 transition-colors duration-200 w-full mt-1"
            >
              <ChevronRight size={16} /> Vai al sito
            </Link>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-zinc-200 px-4 md:px-8 py-4 flex items-center gap-4 sticky top-0 z-40">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 text-[#09090B]"
            data-testid="admin-mobile-toggle"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <h1 className="font-heading text-lg font-bold uppercase tracking-wide text-[#09090B] flex-1">
            {navItems.find((i) => i.path === location.pathname)?.label || "Admin"}
          </h1>
          <PWAInstallButton scope="admin" variant="compact" />
          <NotificationBell />
        </header>

        <main className="p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
